/*
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
*/

import { Component, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { RouterOutlet, RouterLink, RouterLinkActive, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthService } from '../services/auth/auth.service';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { MsalBroadcastService } from '@azure/msal-angular';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-main-layout',
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss',
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    CommonModule
  ],
  encapsulation: ViewEncapsulation.None
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  userAccount: AccountInfo | null = null;
  isLoggedIn: boolean = false;
  isMobileMenuOpen: boolean = false;
  isApiMeshExpanded: boolean = false;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private msalBroadcastService: MsalBroadcastService,
    public router: Router
  ) {}

  ngOnInit(): void {
    this.updateAuthStatus();

    // Auto-expand API mesh menu if on API mesh route (mobile only)
    if (this.isApiMeshRouteActive() && window.innerWidth <= 767) {
      this.isApiMeshExpanded = true;
    }

    // Subscribe to authentication status changes from AuthService
    this.authService.authStatus$
      .pipe(takeUntil(this._destroying$))
      .subscribe((isAuthenticated: boolean) => {
        this.isLoggedIn = isAuthenticated;
        this.userAccount = isAuthenticated ? this.authService.getAccount() : null;
      });

    // Subscribe to MSAL broadcast service for interaction status changes
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.authService.refreshAuthStatus();
        this.updateAuthStatus();
      });
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  private updateAuthStatus(): void {
    try {
      this.userAccount = this.authService.getAccount();
      this.isLoggedIn = this.authService.isLoggedIn();
    } catch (error) {
      console.error('Error updating auth status:', error);
      this.userAccount = null;
      this.isLoggedIn = false;
    }
  }

  login(): void {
    try {
      this.authService.login();
    } catch (error) {
      console.error('Login error:', error);
    }
  }

  logout(): void {
    try {
      this.authService.logout();
      this.router.navigate(['/']);
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  isApisRouteActive(): boolean {
    const currentUrl = this.router.url;
    return currentUrl === '/apis' || currentUrl.startsWith('/api-viewer');
  }

  isApiMeshRouteActive(): boolean {
    const currentUrl = this.router.url;
    return currentUrl.startsWith('/api-mesh');
  }

  toggleApiMeshExpanded(): void {
    // Only toggle on mobile devices
    if (window.innerWidth <= 767) {
      this.isApiMeshExpanded = !this.isApiMeshExpanded;
      // Don't close mobile menu when toggling submenu
    }
  }

  isApiMeshOverviewActive(): boolean {
    const currentUrl = this.router.url;
    return currentUrl === '/api-mesh' || currentUrl.includes('/api-mesh/overview');
  }

  isApiMeshExplorerActive(): boolean {
    return this.router.url.includes('/api-mesh/explorer');
  }

  isApiMeshHaystackActive(): boolean {
    return this.router.url.includes('/api-mesh/haystack');
  }

  isApiMeshSubmenuActive(): boolean {
    // Only highlight parent when a specific submenu item is active
    return this.isApiMeshOverviewActive() || this.isApiMeshExplorerActive() || this.isApiMeshHaystackActive();
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  getUserDisplayName(): string {
    if (!this.userAccount) return '';

    const name = this.userAccount.name || this.userAccount.username || '';
    // For mobile, show shorter name
    const firstName = name.split(' ')[0];
    return firstName.length > 12 ? firstName.substring(0, 12) + '...' : firstName;
  }
}
